import { useContext, useState } from "react";
import { DynamicFormDefinition, FormContainer, FormDefinitionPostRequest, FormStatsResponse } from "../../common";
import { Alert, AlertColor, Button, Card, CardContent, Dialog, Snackbar } from "@mui/material";
import './Forms.scss';
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "../../authConfig";
import { DependencyInjectionContext } from "../../contexts/dependency-injection-context";
import FormsList from "./FormsList/FormsList";
import { EDIT, FormListEvent, PREVIEW, STATS, SAMPLE } from "./FormsList/forms-list-events";
import JsonEditor from './JsonEditor/JsonEditor';
import { Resizeable } from '../../components/core/Resizeable/Resizeable';
import FormDetailsModal from "./FormDetails/FormDetailsModal";
import { ClientSelector } from "../../components/core/ClientSelector/ClientSelector";
import { LoadingIndicator } from "../../components/core/LoadingIndicator/LoadingIndicator";

export const Forms = (props: any) => {

    const authRequest = {
        ...loginRequest
    };

    const [forms, setForms] = useState<DynamicFormDefinition[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentForm, setCurrentForm] = useState<DynamicFormDefinition | undefined>(undefined);
    const [formStatsOpen, setFormStatsOpen] = useState(false);
    const [formStats, setFormStats] = useState<FormStatsResponse>();
    const [selectedForm, setSelectedForm] = useState<DynamicFormDefinition>();
    const [selectedClientId, setSelectedClientId] = useState<string>();
    const [editForm, setEditForm] = useState<DynamicFormDefinition>();
    const [newFormModalOpen, setNewFormModalOpen] = useState<boolean>(false);
    const [newForm, setNewForm] = useState<DynamicFormDefinition>(getNewForm());

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertText, setAlertText] = useState<string>();
    const [alertType, setAlertType] = useState<AlertColor>();

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setAlertOpen(false);
    };
    const { formsApi, applicationState } = useContext(DependencyInjectionContext);

    function getNewForm(): DynamicFormDefinition {
        return { controls: [], id: "", isOfflineForm: false, isWebForm: false, name: "", revision: 0, successMessage: null };
    }

    const getForms = async (clientId: string | null = null) => {
        setLoading(true);

        let request = null;
        if (clientId != null) {
            request = {
                clientId: clientId,
                onlyOfflineForms: false
            };
        }

        const forms = await formsApi.fetchFormDefinitions(request);
        setForms(forms);
        setLoading(false);
    };

    const downloadFileFromString = (text: string, filename: string) => {
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const formListActionClick = async (event: FormListEvent, form: DynamicFormDefinition, clientId: string | undefined) => {
        switch (event) {
            case EDIT:
                setEditForm(form);
                break;
            case PREVIEW:
                setCurrentForm(form);
                break;
            case STATS:
                await showFormStats(form, clientId);
                break;
            case SAMPLE:
                generateSampleJson(form);
                break;
            default:
                alert(`${event} is not implemented!`);
                break;
        }
    }

    const generateSampleJson = (form: DynamicFormDefinition) => {
        const formJson: any = {};

        form?.controls.forEach(control => {
            if (control.skipSerialization) {
                return;
            }

            const propertyName = control.jsonName == null ? camelCase(control.label || '') : control.jsonName;
            const value = control.state ?? "";
            formJson[propertyName] = value;
        });

        var jsonString = JSON.stringify(formJson);

        downloadFileFromString(jsonString, `${form.name}.json`);
    }

    function camelCase(str: string) {
        return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word: string, index: number) {
            return index === 0 ? word.toLowerCase() : word.toUpperCase();
        }).replace(/\s+/g, '');
    }

    const showFormStats = async (form: DynamicFormDefinition, clientId: string | undefined) => {
        setLoading(true);

        let url = `FormStats?formDefinitionId=${form.id}`;
        if (clientId != null) {
            url = `${url}&clientId=${clientId}`;
        }

        const stats = await formsApi.get<FormStatsResponse>(url);
        setSelectedForm(form);

        setFormStats(stats);
        setFormStatsOpen(true);

        setLoading(false);
    }

    const handleClose = (value: string) => {
        setFormStatsOpen(false);
    };

    const onClientSelected = async (clientId: string) => {
        setSelectedClientId(clientId);
        applicationState.selectedClientId = clientId;
        getForms(clientId);
    };

    const saveForm = async (form: DynamicFormDefinition) => {
        try {
            const request: FormDefinitionPostRequest = {
                clientId: selectedClientId,
                formDefinition: form
            };

            const newForm = await formsApi.post<FormDefinitionPostRequest, DynamicFormDefinition>('/FormDefinitionsSave', request);
            setEditForm(newForm);

            const updatedFormIndex = forms?.findIndex(x => x.id === newForm.id);
            if (updatedFormIndex != null) {
                const newForms = forms?.filter(x => x.id !== newForm.id);
                newForms?.splice(updatedFormIndex, 0, newForm);
                setForms(newForms);
            }

            showAlert('Form saved!', 'success');
        } catch (error) {
            showAlert('Error saving form, please try again later.', 'error');
        }
    }

    const showAlert = (text: string, type: AlertColor) => {
        setAlertText(text);
        setAlertType(type);
        setAlertOpen(true);
    }

    return (
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={authRequest}
        >
            <LoadingIndicator loading={loading}></LoadingIndicator>

            {!editForm &&
                <div className="page forms-page">
                    <h1>Forms</h1>

                    <div style={{ display: editForm ? 'none' : 'block', paddingTop: '20px' }}>
                        <ClientSelector onClientSelected={onClientSelected}></ClientSelector>
                    </div>

                    <div style={{ paddingBottom: '10px' }}>
                        <Button variant="contained" onClick={() => {
                            setNewForm(getNewForm());
                            setNewFormModalOpen(true);
                        }}>Create new form</Button>
                    </div>
                    <FormsList forms={forms} callback={formListActionClick} clientId={selectedClientId}></FormsList>

                    {currentForm &&
                        (
                            <div>
                                <h1>{currentForm.name}</h1>
                                <FormContainer form={currentForm} hideSubmit loading={false} formLoading={loading}></FormContainer>
                            </div>
                        )
                    }
                </div>
            }
            {editForm &&
                <div style={{ display: "flex", width: '100%' }}>
                    <Resizeable
                        left={<JsonEditor form={editForm} setForm={form => {
                            setEditForm(form);
                        }}
                            closeForm={() => setEditForm(undefined)}
                            saveForm={saveForm}></JsonEditor>}
                        right={<div style={{ height: '100%', overflowY: 'auto' }}><FormContainer form={editForm} hideSubmit loading={false} formLoading={loading}></FormContainer></div>}
                        min={500}
                    />
                </div>
            }

            <Dialog open={formStatsOpen} onClose={handleClose}>
                <Card sx={{ minWidth: 275 }}>
                    <CardContent>
                        <h3 style={{ margin: 0 }}>{selectedForm?.name}</h3>
                        <hr></hr>
                        <div><b>Total Submissions:</b> {formStats?.submissionCountTotal}</div>
                        <div><b>Total Views:</b> {formStats?.viewCountTotal}</div>
                        <p></p>
                        <div><b>Submissions in past 7 days:</b> {formStats?.submissionCountLast7}</div>
                        <div><b>Views in past 7 days:</b> {formStats?.viewCountLast7}</div>
                    </CardContent>
                </Card>
            </Dialog>

            <Snackbar open={alertOpen} autoHideDuration={6000} onClose={handleCloseAlert} sx={{ width: '30%' }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleCloseAlert} severity={alertType} sx={{ width: '100%' }}>
                    {alertText}
                </Alert>
            </Snackbar>

            <FormDetailsModal form={newForm} isOpen={newFormModalOpen}
                saveForm={(form) => {
                    setEditForm(form)
                }}
                closeForm={() => {
                    setNewFormModalOpen(false);
                    setNewForm(getNewForm());
                }}></FormDetailsModal>
        </MsalAuthenticationTemplate >
    );
};